import { Skeleton, Tab } from "@mui/material";
import Plus from "assets/img/svg/plus";
import DeleteModal from "components/shared/DeleteModal";
import ModalBase from "components/shared/ModalBase";
import StyledButton from "components/shared/StyledButton";
import { StyledTabs } from "components/shared/StyledTabs";
import TitleSeparation from "components/shared/TitleSeparation";
import type { FieldData, TFieldsDetails } from "formFields";
import { beneficiaryFormFields, publicationFormFields, termFormFields, titleFormFields } from "formFields";
import { resolvePressChannelSlot } from "helpers/pressCalculationBase";
import { useAbility } from "hooks/useAbility";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import type { ICustomField } from "store/customFieldsStore";
import { deleteCustomField } from "store/customFieldsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { EPortalType, type TPortalType } from "store/types";
import type { SubsetOf } from "types";

import ChangeSectionModal from "./ChangeSectionModal";
import CustomFieldForm, { getValueTypeOptions } from "./CustomFieldForm";
import CustomFieldLine from "./CustomFieldLine";
import { getDisplayedNativeFields } from "./helpers";
import { METADATA_PATH, metadataPermission } from "./metadataTabs";
import type { MetadataResource } from "./metadataTabs";
import styles from "./style.module.scss";

const bookPublisherType = (publisherType: TPortalType | undefined): EPortalType.BOOK | undefined =>
    publisherType === EPortalType.BOOK ? EPortalType.BOOK : undefined;

const beneficiaryPublisherType = (
    publisherType: TPortalType | undefined,
): SubsetOf<TPortalType, "press" | "audiovisual" | "book"> | undefined => {
    if (publisherType === "book" || publisherType === "press" || publisherType === "audiovisual") return publisherType;
    return undefined;
};

const nativeFieldsForResource = (
    resource: MetadataResource,
    publisherType: TPortalType | undefined,
    press: Parameters<typeof publicationFormFields>[1],
    book: Parameters<typeof titleFormFields>[1],
) => {
    if (resource === "term") return termFormFields();
    if (resource === "beneficiary") return beneficiaryFormFields(beneficiaryPublisherType(publisherType), book);
    if (publisherType === "press" || publisherType === "audiovisual")
        return publicationFormFields(publisherType, press);
    return titleFormFields(bookPublisherType(publisherType), book);
};

/** Native sections plus the custom fields stored for this resource. The store is the source of truth. */
const sectionsWithCustomFields = (nativeFields: Record<string, FieldData>, customFields: ICustomField[]) => {
    const sections: Record<string, FieldData> = {};
    for (const [key, section] of Object.entries(getDisplayedNativeFields(nativeFields))) {
        sections[key] = { ...section, fields: [...section.fields] };
    }
    for (const field of customFields) {
        const section = field.formLocation ? sections[field.formLocation] : undefined;
        if (!section || section.fields.some((item) => item.uuid === field.uuid)) continue;
        section.fields = [...section.fields, field];
    }
    return sections;
};

const metadataLabelKeys = (resource: MetadataResource, publisherType: string | undefined) => {
    if (resource === "title") {
        return {
            tab: "CustomFields.TabTitles",
            resourceLabel: `CustomFields.Titles_${publisherType}`,
            pageLabel: `CustomFields.TitlePage_${publisherType}`,
        };
    }
    if (resource === "term") {
        return {
            tab: "CustomFields.TabTerms",
            resourceLabel: "CustomFields.Terms",
            pageLabel: "CustomFields.TermPage",
        };
    }
    return {
        tab: "CustomFields.TabBeneficiaries",
        resourceLabel: "CustomFields.Beneficiaries",
        pageLabel: "CustomFields.BeneficiaryPage",
    };
};

interface ICustomForm {
    /**
     * Type of resource
     */
    resource: "title" | "beneficiary" | "term";
    /**
     * Ultimately, if there are more forms, the data should be passed from parent component. As of now, we can handle that from here.
     */
    data?: any;
    /**
     * Does company use Author Portal.
     */
    hasPortal: boolean;
}

/**
 * Reusable container in Settings to display custom fields (title or beneficiay):
 **/
const CustomFieldsContainer = ({ resource, hasPortal }: ICustomForm) => {
    const { channels } = useAppSelector((state) => state.channels);
    const { companyInfos, getCompanySettings } = useCompanySettings();
    const publisherType = companyInfos?.publisherType;
    const preferredChannelId = getCompanySettings("press_preferred_channel");
    const preferredChannel = channels.find((channel) => channel.id === +preferredChannelId);

    // Added for title and benef so it's available but currently not that much to add.
    const conditions = {
        press: {
            usesBothCalculationMethods: !!getCompanySettings("press_uses_multiple_channels"),
            isFolioBased: resolvePressChannelSlot(preferredChannel ?? { name: "", type: null }) === "folio",
            isPreferredIdentifierConcat: getCompanySettings("preferred_title_identifier") === "concat",
        },
        book: {
            usesMultipleBrands: getCompanySettings("uses_multiple_brands") === true,
        },
        beneficiary: {},
    };

    const navigate = useNavigate();
    const { pathname } = useLocation();
    // /settings/metadata has no tab of its own: replace it with the tab that is actually shown.
    useEffect(() => {
        const path = METADATA_PATH[resource];
        if (pathname !== path) navigate(path, { replace: true });
    }, [pathname, resource, navigate]);
    const data = useMemo(
        () => nativeFieldsForResource(resource, publisherType, conditions.press, conditions.book),
        [
            resource,
            publisherType,
            conditions.press.usesBothCalculationMethods,
            conditions.press.isFolioBased,
            conditions.press.isPreferredIdentifierConcat,
            conditions.book.usesMultipleBrands,
        ],
    );
    const [t] = useTranslation();
    const ability = useAbility();
    const labelKeys = metadataLabelKeys(resource, publisherType);
    const resourceLabel = t(labelKeys.resourceLabel);
    const pageLabel = t(labelKeys.pageLabel);
    const resourceTabs = (Object.keys(METADATA_PATH) as MetadataResource[])
        .filter((item) => ability.can("access", metadataPermission(item)))
        .map((item) => ({ resource: item, label: t(metadataLabelKeys(item, publisherType).tab) }));
    const valueTypeOptions = useMemo(() => getValueTypeOptions(t), [t]);
    const dispatch = useAppDispatch();

    const [hasCollapsedBeenTriggered, setHasCollapsedBeenTriggered] = useState(false);

    // Filter custom fields specific to title:
    const { customFields } = useAppSelector((state) => state.customFields);
    const resourceCustomFields = useMemo(() => {
        return customFields.filter((field) => field?.relatedResource === resource);
    }, [customFields, resource]);

    const { resourceCustomFieldsStatus: fetchStatus } = useAppSelector((state) => state.customFields);

    const displayedFields = useMemo(
        () => sectionsWithCustomFields(data, resourceCustomFields),
        [data, resourceCustomFields],
    );
    const displayedSections = Object.keys(displayedFields);

    // States needed for handling modals:
    const [modalType, setModalType] = useState("");
    const [chosenSection, setChosenSection] = useState("");
    const [chosenField, setChosenField] = useState<ICustomField | null>(null);
    const [openOnResource, setOpenOnResource] = useState(resource);
    // Close a dialog that belonged to the previous tab. The field list itself follows the store.
    if (openOnResource !== resource) {
        setOpenOnResource(resource);
        setModalType("");
        setChosenSection("");
        setChosenField(null);
        setHasCollapsedBeenTriggered(false);
    }

    const fieldMenuOptions = [
        {
            id: "edit",
            name: t("CustomFields.FieldActions.Update"),
            action: () => {
                setModalType("editField");
            },
        },
        {
            id: "move",
            name: t("CustomFields.FieldActions.Move"),
            action: () => {
                setModalType("moveField");
            },
        },
        {
            id: "delete",
            name: t("CustomFields.FieldActions.Delete"),
            action: () => {
                setModalType("deleteField");
            },
        },
    ];

    const handleFieldDeletion = async () => {
        if (!chosenField) return;
        const response = await dispatch(deleteCustomField({ fieldId: chosenField.id }));
        if (response.meta.requestStatus === "fulfilled") setModalType("");
    };

    const scrollMovedFieldIntoView = () => {
        if (!chosenField) return;
        document.getElementById(`${chosenField.name}_${chosenField.id}`)?.scrollIntoView({ behavior: "smooth" });
        setModalType("");
    };

    // Collapse for showing more info on the custom field:
    const handleCollapse = (field: ICustomField) => {
        if (chosenField?.id === field?.id) {
            setHasCollapsedBeenTriggered(false);
            setChosenField(null);
        } else {
            setChosenField(field);
            setHasCollapsedBeenTriggered(true);
        }
    };

    return (
        <div className={styles["custom-form-page"]}>
            <div className={styles["custom-form-header"]}>
                <h1 className={styles["custom-form-title"]}>{t("CustomFields.Page", { formTitle: pageLabel })}</h1>
                <span>{t("CustomFields.PageDescription", { formTitle: resourceLabel })}</span>
                <StyledTabs
                    className={styles["resource-tabs"]}
                    value={resource}
                    onChange={(_event, next: "beneficiary" | "title" | "term") => {
                        if (next !== resource && resourceTabs.some((tab) => tab.resource === next)) {
                            navigate(METADATA_PATH[next]);
                        }
                    }}
                >
                    {resourceTabs.map((tab) => (
                        <Tab key={tab.resource} value={tab.resource} label={tab.label} />
                    ))}
                </StyledTabs>
            </div>

            {displayedSections.map((item, index) => {
                const section = displayedFields[item];
                return (
                    <div className={`${styles["form-section"]} ${index === 0 ? styles["first"] : ""}`} key={item}>
                        <TitleSeparation title={section?.label} />
                        {section &&
                            section?.fields.map((field) => {
                                const valueType = (field as TFieldsDetails).native
                                    ? ""
                                    : valueTypeOptions.find((type) => type.value === field.valueType)?.label;
                                return (field as TFieldsDetails).native ? (
                                    <div className={styles["form-field"]} key={(field as TFieldsDetails).key}>
                                        <div className={styles["left-line"]}></div>
                                        <div className={styles["field-info"]}>
                                            <span className={styles["field-name"]}>{field.name}</span>
                                            <span className={styles["field-native"]}>{t("CustomFields.Native")}</span>
                                        </div>
                                    </div>
                                ) : fetchStatus === "pending" ? (
                                    <div className={styles["field-skeleton"]}>
                                        <Skeleton width={200} height={30} />
                                        <Skeleton width={30} height={30} />
                                    </div>
                                ) : (
                                    <CustomFieldLine
                                        key={field.uuid}
                                        field={field as ICustomField}
                                        chosenField={chosenField}
                                        setChosenField={setChosenField}
                                        section={section}
                                        setChosenSection={setChosenSection}
                                        fieldMenuOptions={fieldMenuOptions}
                                        handleCollapse={handleCollapse}
                                        valueType={valueType}
                                        hasCollapseBeenTriggered={hasCollapsedBeenTriggered}
                                    />
                                );
                            })}
                        <div className={styles["add-field-btn"]}>
                            {section.showOnForm && (
                                <StyledButton
                                    buttonType="outlined"
                                    onClick={() => {
                                        setModalType("addField");
                                        setChosenSection(section?.key);
                                    }}
                                >
                                    <Plus />
                                    {t("CustomFields.FieldActions.Add")}
                                </StyledButton>
                            )}
                            {!section.showOnForm && (
                                <span className={styles["disabled-text"]}>
                                    {t("CustomFields.NoCustomFieldsAllowed")}
                                </span>
                            )}
                        </div>
                    </div>
                );
            })}

            <ModalBase
                open={modalType === "addField"}
                onClose={() => setModalType("")}
                modalTitle={t("CustomFields.FormModal.AddTitle")}
                modalSubtitle={t("CustomFields.FormModal.AddSubtitle", {
                    formTitle: resourceLabel,
                })}
            >
                <CustomFieldForm
                    chosenSection={chosenSection}
                    resource={resource}
                    setModalType={setModalType}
                    mode="new"
                    hasPortal={hasPortal}
                />
            </ModalBase>

            <ModalBase
                open={modalType === "editField"}
                onClose={() => setModalType("")}
                modalTitle={t("CustomFields.FormModal.EditTitle")}
                modalSubtitle={t("CustomFields.FormModal.EditSubtitle", {
                    formTitle: resourceLabel,
                })}
            >
                <CustomFieldForm
                    chosenSection={chosenSection}
                    resource={resource}
                    setModalType={setModalType}
                    mode="edit"
                    chosenField={chosenField}
                    onUpdate={() => setModalType("")}
                    hasPortal={hasPortal}
                />
            </ModalBase>

            <ModalBase
                open={modalType === "moveField"}
                onClose={() => setModalType("")}
                modalTitle={t("CustomFields.FormModal.MoveTitle")}
                modalSubtitle={t("CustomFields.FormModal.MoveSubtitle")}
            >
                <ChangeSectionModal
                    sections={displayedSections.filter((section) => displayedFields[section]?.showOnForm)}
                    fields={displayedFields}
                    chosenField={chosenField}
                    onUpdate={scrollMovedFieldIntoView}
                />
            </ModalBase>

            <DeleteModal
                open={modalType === "deleteField"}
                onClose={() => setModalType("")}
                onDelete={handleFieldDeletion}
                elementTitle={`${t("CustomFields.DeleteField", { fieldName: chosenField?.name ?? "" })}`}
            />
        </div>
    );
};

export default CustomFieldsContainer;
